import React from "react";
import { Box, Collapse, IconButton } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TimelineIcon from "@mui/icons-material/Timeline";
import DrillRow, { DRILL_ROW_SELECTED_BG } from "@/features/routeTree/DrillRow.jsx";
import WeatherBanner from "@/features/routeTree/WeatherBanner.jsx";
import KpiTile from "@/components/ui/KpiTile";
import WarnChip from "@/components/ui/WarnChip";
import summaryWarnChips from "@/features/route/summaryWarnChips";
import routeKpis from "@/features/route/routeKpis";

export default function RouteRowCard({
  route,
  primary,
  secondary,
  badgeSlot,
  actionSlot,
  selected = false,
  muted = false,
  expanded = false,
  onToggleExpanded,
  onRowClick,
  staleness,
  onRecalculate,
  recalculating = false,
  recalcBlocked = false,
  recalcError = null,
  onDismissError,
}) {
  const summary = route?.summary ?? null;
  const chips = expanded ? summaryWarnChips(summary) : [];
  const kpis = expanded ? routeKpis(summary) : [];

  const expandButton = (
    <IconButton
      size="small"
      aria-label={expanded ? "Collapse route details" : "Expand route details"}
      aria-expanded={expanded}
      // Stop here: the row click would toggle the map selection.
      onClick={(e) => {
        e.stopPropagation();
        onToggleExpanded?.();
      }}
      sx={{ color: muted ? "#7b8a99" : "#004f7d", p: 0, flexShrink: 0 }}
    >
      <KeyboardArrowDownIcon
        sx={{
          fontSize: 18,
          transition: "transform 150ms",
          transform: expanded ? "rotate(180deg)" : "none",
        }}
      />
    </IconButton>
  );

  return (
    // Row and details share one selection frame (left bar + background).
    <Box
      sx={{
        borderRadius: "10px",
        mb: 0.25,
        overflow: "hidden",
        borderLeft: selected ? "4px solid #004f7d" : "4px solid transparent",
        bgcolor: selected ? DRILL_ROW_SELECTED_BG : "transparent",
      }}
    >
      <DrillRow
        embedded
        icon={<TimelineIcon sx={{ fontSize: 16 }} />}
        primary={primary}
        primaryExtra={expandButton}
        secondary={secondary}
        muted={muted}
        selected={selected}
        showChevron={false}
        onClick={onRowClick}
        badge={badgeSlot}
        action={actionSlot}
      />
      <Collapse in={expanded} unmountOnExit>
        <Box
          data-testid="route-card-body"
          sx={{
            px: 1,
            pb: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
          }}
        >
          <WeatherBanner
            staleness={staleness}
            routeState={route?.state}
            onUpdate={onRecalculate}
            updating={recalculating}
            blocked={recalcBlocked}
          />
          {recalcError ? (
            <WarnChip
              severity="error"
              onClick={onDismissError}
              sx={{ cursor: onDismissError ? "pointer" : "default" }}
            >
              {recalcError}
            </WarnChip>
          ) : null}
          {chips.length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {chips.map((chip) => (
                <WarnChip key={chip.key} severity={chip.severity}>
                  {chip.label}
                </WarnChip>
              ))}
            </Box>
          ) : null}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: 0.75,
            }}
          >
            {kpis.map((kpi) => (
              <KpiTile
                key={kpi.key}
                label={kpi.label}
                value={kpi.value}
                unit={kpi.unit}
                sx={{ bgcolor: "#fff", border: "1px solid #e9eff4", minWidth: 0 }}
              />
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
